import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { PageLoading } from '@bonusly/common-page-loading';
import {
	AbsoluteCenter,
	Button,
	Display,
	Image,
	Link,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Stack,
	Text,
} from '@bonusly/design-system';

import logo from '../../../../assets/images/bonusly-logo.png';
import { PromoModal, usePromoDismiss } from '../../../promo/promo-modal';
import { useGetReviewSolicitation, useRecordReviewSolicitation, useRemindLaterReviewSolicitation } from '../../hooks/use-review-solicitations';

const PROMO_NAME = 'g2-review-v1';
const G2_PRIVACY_POLICY_URL = 'https://legal.g2.com/privacy-policy';
const G2_TERMS_OF_USE_URL = 'https://legal.g2.com/terms-of-use';

export function ReviewSolicitationsModal() {
	const { data: reviewSolicitation, isLoading: isSolicitationLoading } = useGetReviewSolicitation();
	const { mutate: recordReviewSolicitation } = useRecordReviewSolicitation();
	const { mutate: remindLaterReviewSolicitation } = useRemindLaterReviewSolicitation();
	const { trackEvent } = useAnalytics();

	const solicitReview = !!reviewSolicitation?.shouldSolicit;
	// Backend is the single source of truth for the incentivized flag — it only sets
	// `incentivized: true` on the response when `g2-incentive-survey` is enabled for the
	// current user. Checking the flag again here would just add an Amplitude round-trip
	// and risk frontend/backend disagreement.
	const showIncentivized = !!reviewSolicitation?.incentivized;

	// Lets us close the modal after Remind me later without calling PromoModal's permanent
	// dismiss — flipping `enabled` to false makes PromoModal go ineligible this session
	// (and trigger a winner recomputation), while the backend remind_later event (48h
	// window) drives visibility on subsequent page loads.
	const [remindLaterClicked, setRemindLaterClicked] = useState(false);
	const [reviewStarted, setReviewStarted] = useState(false);
	const [reviewLoaded, setReviewLoaded] = useState(false);

	// Stay enabled while we don't yet know the answer (keeps PromoModal's status `pending`
	// via the `isLoading` prop, holding our queue slot) and while we know we should solicit.
	// Flip to false only once the query resolves with shouldSolicit=false so the queue
	// recomputes and another promo can take the slot.
	const enabled = !remindLaterClicked && (isSolicitationLoading || solicitReview);

	const onStartReview = useCallback(() => {
		setReviewStarted(true);
		recordReviewSolicitation();
		trackEvent(AnalyticsEvent.Onboarding.ReviewSolicitationsModalStartReviewClicked);
	}, [recordReviewSolicitation, trackEvent]);

	const onRemindLater = useCallback(() => {
		setRemindLaterClicked(true);
		remindLaterReviewSolicitation();
		trackEvent(AnalyticsEvent.Onboarding.ReviewSolicitationsModalRemindLaterClicked);
	}, [remindLaterReviewSolicitation, trackEvent]);

	const onReviewLoaded = useCallback(() => {
		setReviewLoaded(true);
	}, []);

	const recordDismiss = useCallback(() => {
		// handle closing without starting the review; still record so the backend's
		// 60-day / 6-month cooldown is honored on top of PromoModal's permanent dismiss.
		if (!reviewStarted) recordReviewSolicitation();
		trackEvent(AnalyticsEvent.Onboarding.ReviewSolicitationsModalDismissed);
	}, [reviewStarted, recordReviewSolicitation, trackEvent]);

	const modalSize = {
		base: reviewStarted ? 'full' : 'lg',
		xs: reviewStarted ? '5xl' : 'lg',
	};

	return (
		<PromoModal
			name={PROMO_NAME}
			enabled={enabled}
			isLoading={isSolicitationLoading}
			size={modalSize}
			isCentered
			closeOnOverlayClick={false}
			closeOnEsc={false}
		>
			<ModalOverlay />
			<ReviewSolicitationsModalInner
				showIncentivized={showIncentivized}
				reviewStarted={reviewStarted}
				reviewLoaded={reviewLoaded}
				g2ReviewLink={reviewSolicitation?.g2ReviewLink}
				onStartReview={onStartReview}
				onRemindLater={onRemindLater}
				onReviewLoaded={onReviewLoaded}
				onRecordDismiss={recordDismiss}
			/>
		</PromoModal>
	);
}

interface ReviewSolicitationsModalInnerProps {
	showIncentivized: boolean;
	reviewStarted: boolean;
	reviewLoaded: boolean;
	g2ReviewLink: string | undefined;
	onStartReview: () => void;
	onRemindLater: () => void;
	onReviewLoaded: () => void;
	onRecordDismiss: () => void;
}

function ReviewSolicitationsModalInner({
	showIncentivized,
	reviewStarted,
	reviewLoaded,
	g2ReviewLink,
	onStartReview,
	onRemindLater,
	onReviewLoaded,
	onRecordDismiss,
}: ReviewSolicitationsModalInnerProps) {
	const { t } = useTranslation('onboarding', {
		keyPrefix: 'review_solicitations_modal',
	});
	const { trackEvent } = useAnalytics();
	const promoDismiss = usePromoDismiss();

	// Fires exactly when PromoModal actually displays this modal (eligible + queue winner),
	// so analytics match what the user saw rather than what the backend authorized.
	useEffect(() => {
		trackEvent(AnalyticsEvent.Onboarding.ReviewSolicitationsModalViewed);
	}, [trackEvent]);

	const onCloseViaX = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			// Prevent Chakra from invoking PromoModal's onClose (which would double-fire dismiss).
			event.preventDefault();
			onRecordDismiss();
			promoDismiss();
		},
		[onRecordDismiss, promoDismiss]
	);

	if (reviewStarted) {
		return (
			<ModalContent minHeight="90vh">
				<ModalHeader>{t('review_frame_title')}</ModalHeader>
				<ModalCloseButton onClick={onCloseViaX} />
				<ModalBody as={Stack} position="relative" p={0}>
					<AbsoluteCenter>{!reviewLoaded && <PageLoading />}</AbsoluteCenter>
					<iframe
						style={{
							position: 'absolute',
							visibility: reviewLoaded ? 'visible' : 'hidden',
						}}
						height="100%"
						width="100%"
						title={t('review_frame_title')}
						src={g2ReviewLink}
						onLoad={onReviewLoaded}
						data-testid="review-frame"
					/>
				</ModalBody>
			</ModalContent>
		);
	}

	return (
		<ModalContent>
			<ModalCloseButton onClick={onCloseViaX} />
			<ModalBody as={Stack} gap={3} pt={4}>
				<Stack alignItems="center" gap={4}>
					<Image alt={t('company_logo')} h={rem(36)} src={logo} />
					<Display fontWeight="extrabold" fontSize={300}>
						{showIncentivized ? t('incentivized.title') : t('title')}
					</Display>
				</Stack>
				<Text fontSize="md" textAlign="center">
					{showIncentivized ? t('incentivized.prompt') : t('prompt')}
				</Text>
				<Text fontSize="xs" color="text.softest" textAlign="center">
					<Trans t={t} i18nKey="fine_print">
						<Link target="_blank" href={G2_PRIVACY_POLICY_URL} />
						<Link target="_blank" href={G2_TERMS_OF_USE_URL} />
					</Trans>
				</Text>
			</ModalBody>

			<ModalFooter gap={2} justifyContent="center">
				<Button colorScheme="secondary" onClick={onRemindLater} size="md" tabIndex={2} variant="alpha">
					{showIncentivized ? t('incentivized.later') : t('later')}
				</Button>
				<Button colorScheme="primary" onClick={onStartReview} size="md" tabIndex={1} variant="solid">
					{showIncentivized ? t('incentivized.cta') : t('cta')}
				</Button>
			</ModalFooter>
		</ModalContent>
	);
}
